import React, { createContext, useContext, useEffect, useState } from 'react';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import { StyledEngineProvider } from '@mui/material/styles';
import { GlobalStyles } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import customTheme from './components/customTheme';
import customDarkTheme from './components/customThemeDark';
import { Outlet } from 'react-router-dom';
import NRLayout from './components/NRLayout/NRLayout';
import { initPendo } from './utils/pendo.utils';
import { useAuth0 } from '@auth0/auth0-react';
import NRLoadingPage from './pages/NRLoadingPage/NRLoadingPage';
import { LAST_ORG_USED_KEY } from './utils/ApiConstants';
import NRReportFilterContextWrapper from './pages/NRReportTabs/NRReportFilterContextWrapper';
import NRSBOMDataShare from './pages/NRSBOMTable/NRSBOMDataShare';
import NRComponentContextWrapper from './ContextWrappers/NRComponentContextWrapper';
import NRAssetsDataShare from './DataShare/NRAssetsDataShare';
import { DARK_APPEARANCE, LIGHT_APPEARANCE, SELECTED_APPEARANCE, SYSTEM_APPEARANCE } from './constants/constants';
import NRSnackbarProvider from './ContextWrappers/NRSnackbarProvider';
import NRRemediationContextWrapper from './components/NRRemediation/NRRemediationContext';
import NRReactGridContextWrapper from './components/NRReactGrid/NRReactGridContext';
import AssetGroupContextWrapper from './components/AssetGroup/assetGroupContextWrapper';
import { QueryParamProvider } from 'use-query-params';
import queryString from 'query-string';
import useUrlInterceptor from './services/useUrlInterceptor';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import IdleLogoutHandler from './NRIdle';
import NRCheckOrganizationAccess from './auth/NRCheckOrganizationAccess';
import GenericContextWrapper from './components/GenericContext/GenericContextWrapper';
import NROrgLevelSettingsContextWrapper from './pages/NRSettings/NRGeneralSettings/NROrgLevelSettingsContextWrapper';
import { OFREPWebProvider } from '@openfeature/ofrep-web-provider';
import { OpenFeature } from '@openfeature/web-sdk';
import { OpenFeatureProvider } from '@openfeature/react-sdk';

export const UserContext = createContext({
  currentOrg: null,
  setCurrentOrg: () => {},
  currentOrgId: null,
  setCurrentOrgId: () => {},
  orgs: null,
  setOrgs: () => {},
  idToken: null,
  setIdToken: () => {},
  // REMOVE(settings-users-and-access): Auth0 role claims; drop when flag is retired.
  roles: null,
  setRoles: () => {},
  email: null,
  setEmail: () => {},
  isChangingOrg: null,
  setIsChangingOrg: () => {},
  appearanceOptionName: null,
  setAppearanceOptionName: () => {}
});

// This component ensures the user is authenticated before rendering the app.
const CheckAuthentication = ({ element }) => {
  const { setCurrentOrg, setCurrentOrgId, setOrgs, setRoles, setEmail, isChangingOrg, setIdToken } = useContext(UserContext);
  const { isAuthenticated, loginWithRedirect, getAccessTokenSilently, getIdTokenClaims, isLoading } = useAuth0();
  const [loggedIn, setLoggedIn] = useState(false);

  const getTokens = async () => {
    let idToken = await getIdTokenClaims({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });
    const auth0Namespace = process.env.REACT_APP_AUTH0_NAMESPACE;
    const orgsNamespace = auth0Namespace + 'orgs';
    const currentOrgNamespace = auth0Namespace + 'org_name';
    // REMOVE(settings-users-and-access): Auth0 roles claim namespace; drop when flag is retired.
    const rolesNamespace = auth0Namespace + 'roles';
    if (!idToken.org_id) {
      const lastOrgUsedId = localStorage.getItem(idToken.email + LAST_ORG_USED_KEY);
      let userOrg;
      if (lastOrgUsedId) {
        userOrg = lastOrgUsedId;
      } else if (idToken[orgsNamespace]?.length) {
        userOrg = idToken[orgsNamespace][0]?.Id;
      }
      try {
        idToken = await getAccessTokenSilently({
          ignoreCache: true,
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
          organization: userOrg
        });
        idToken = await getIdTokenClaims({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });
      } catch (e) {
        console.log(e);
      }
      setLoggedIn(true);
    } else {
      setLoggedIn(true);
    }
    if (process.env.REACT_APP_ENVIRONMENT !== 'local') {
      initPendo(idToken);
    }
    setOrgs(idToken[orgsNamespace]);
    setEmail(idToken.email);
    setCurrentOrgId(idToken.org_id);
    if (idToken[currentOrgNamespace]) {
      setCurrentOrg(idToken[currentOrgNamespace]);
    }
    // REMOVE(settings-users-and-access): Seed org-selector tags from Auth0 until RBAC is always on.
    if (idToken[rolesNamespace]) {
      setRoles(idToken[rolesNamespace]);
    }
    setIdToken(idToken);
    return idToken;
  };

  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      loginWithRedirect();
    } else if (isAuthenticated && !isLoading) {
      getTokens();
    }
  }, [isAuthenticated, isLoading]);

  return loggedIn && !isChangingOrg ? element : <NRLoadingPage />;
};

const UrlInterceptor = ({ children }) => {
  useUrlInterceptor();
  return children;
};

const idleTimeoutSeconds = process.env.REACT_APP_AUTH0_NAMESPACE + 'idle_seconds';

function App() {
  const [currentOrg, setCurrentOrg] = useState();
  const [currentOrgId, setCurrentOrgId] = useState();
  const [orgs, setOrgs] = useState();
  const [idToken, setIdToken] = useState();
  // REMOVE(settings-users-and-access): Auth0 roles state; drop when flag is retired.
  const [roles, setRoles] = useState();
  const [email, setEmail] = useState();
  const [isChangingOrg, setIsChangingOrg] = useState();
  const [appearanceOptionName, setAppearanceOptionName] = useState(SYSTEM_APPEARANCE);
  const [selectedDarkTheme, setSelectedDarkTheme] = useState(
    appearanceOptionName === DARK_APPEARANCE ||
      (appearanceOptionName === SYSTEM_APPEARANCE && window.matchMedia('(prefers-color-scheme: dark)').matches)
  );

  // Helpers to get and set appearance in localStorage
  const getSelectedAppearance = email => localStorage.getItem(`${email}${SELECTED_APPEARANCE}`);
  function setAppearance(themeSelector) {
    switch (themeSelector) {
      case DARK_APPEARANCE:
        return true;
      case LIGHT_APPEARANCE:
        return false;
      case SYSTEM_APPEARANCE:
        return window.matchMedia('(prefers-color-scheme: dark)').matches;
      default:
        return false;
    }
  }

  useEffect(() => {
    if (email) {
      setAppearanceOptionName(getSelectedAppearance(email) || LIGHT_APPEARANCE);
    }
  }, [email]);

  useEffect(() => {
    setSelectedDarkTheme(setAppearance(appearanceOptionName));
  }, [appearanceOptionName]);

  const defaultValues = {
    currentOrg,
    setCurrentOrg,
    currentOrgId,
    setCurrentOrgId,
    orgs,
    setOrgs,
    idToken,
    setIdToken,
    roles,
    setRoles,
    email,
    setEmail,
    isChangingOrg,
    setIsChangingOrg,
    appearanceOptionName,
    setAppearanceOptionName
  };

  // Listen for system appearance changes
  useEffect(() => {
    if (email) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const listener = ({ matches }) => {
        if (getSelectedAppearance(email) === SYSTEM_APPEARANCE) {
          setSelectedDarkTheme(matches);
        }
      };
      mediaQuery.addEventListener('change', listener);
      return () => mediaQuery.removeEventListener('change', listener);
    }
  }, [email]);

  useEffect(() => {
    // Poll flag evaluations over plain HTTPS. The relay proxy is fronted by an
    // HTTP/2 load balancer that does not support the WebSocket upgrade the
    // previous provider relied on, which caused intermittent startup failures.
    const ofrepWebProvider = new OFREPWebProvider({
      baseUrl: process.env.REACT_APP_FEATURE_FLAG_URL,
      headers: [['Authorization', `Bearer ${process.env.REACT_APP_FEATURE_FLAG_API_KEY}`]],
      changeDetection: 'polling',
      pollInterval: 60000
    });

    // Instantiate and set our provider (be sure this only happens once)!
    // Note: there's no need to await its initialization, the React SDK handles re-rendering and suspense for you!
    OpenFeature.setProvider(ofrepWebProvider);
  }, []);

  useEffect(() => {
    // Target evaluations by organization and expose org/user ids as attributes
    // for future targeting rules. Defaults keep evaluations stable before login.
    // The provider re-evaluates flags whenever the context changes.
    const NOT_LOGGED_IN = 'not-logged-in';
    OpenFeature.setContext({
      targetingKey: currentOrgId || NOT_LOGGED_IN,
      org_id: currentOrgId || NOT_LOGGED_IN,
      user_id: idToken?.sub || NOT_LOGGED_IN
    });
  }, [currentOrgId, idToken]);

  return (
    <StyledEngineProvider injectFirst>
      <UserContext.Provider value={defaultValues}>
        <ThemeProvider theme={selectedDarkTheme ? customDarkTheme : customTheme}>
          <CssBaseline enableColorScheme />
          <GlobalStyles
            styles={{
              body: {
                fontSize: '0.875rem',
                lineHeight: 1.43,
                margin: 0,
                fontFamily: `'Roboto', '"Helvetica Neue"', 'Arial', 'sans-serif', '-apple-system'`,
                WebkitFontSmoothing: 'antialiased',
                MozOsxFontSmoothing: 'grayscale'
              },
              // Needed for print the collapses as expanded even though they are closed.
              '@media print': {
                '.MuiCollapse-root': {
                  height: 'auto !important',
                  minHeight: 'auto !important',
                  overflow: 'visible !important',
                  visibility: 'visible !important',
                  display: 'block !important'
                },
                '.MuiCollapse-wrapper': {
                  height: 'auto !important',
                  minHeight: 'auto !important'
                },
                '.MuiCollapse-wrapperInner': {
                  height: 'auto !important',
                  minHeight: 'auto !important',
                  overflow: 'visible !important',
                  display: 'block !important'
                }
              }
            }}
          />
          <NRSnackbarProvider>
            <QueryParamProvider
              adapter={ReactRouter6Adapter}
              options={{
                searchStringToObject: queryString.parse,
                objectToSearchString: queryString.stringify
              }}
            >
              <UrlInterceptor>
                <CheckAuthentication
                  element={
                    <OpenFeatureProvider>
                      <GenericContextWrapper>
                        <NROrgLevelSettingsContextWrapper>
                          <NRComponentContextWrapper>
                            <NRAssetsDataShare>
                              <NRSBOMDataShare>
                                <NRReactGridContextWrapper>
                                  <NRRemediationContextWrapper>
                                    <AssetGroupContextWrapper>
                                      <NRLayout>
                                        {idToken && idToken[idleTimeoutSeconds] && <IdleLogoutHandler />}
                                        <NRCheckOrganizationAccess>
                                          <NRReportFilterContextWrapper>
                                            <Outlet />
                                          </NRReportFilterContextWrapper>
                                        </NRCheckOrganizationAccess>
                                      </NRLayout>
                                    </AssetGroupContextWrapper>
                                  </NRRemediationContextWrapper>
                                </NRReactGridContextWrapper>
                              </NRSBOMDataShare>
                            </NRAssetsDataShare>
                          </NRComponentContextWrapper>
                        </NROrgLevelSettingsContextWrapper>
                      </GenericContextWrapper>
                    </OpenFeatureProvider>
                  }
                />
              </UrlInterceptor>
            </QueryParamProvider>
          </NRSnackbarProvider>
        </ThemeProvider>
      </UserContext.Provider>
    </StyledEngineProvider>
  );
}

export default App;